import type { TaskCommandType, TaskDetailDto, TaskRequestDto } from '@ovo/shared'
import { useState } from 'react'
import { Link, useParams } from 'react-router'
import { ApiError, errorMessage } from '../api/client.ts'
import { useAddComment, useMe, useTask } from '../api/hooks.ts'
import { formatDue, formatMoment, overdueText } from '../lib/dates.ts'
import { useNow } from '../lib/hooks.ts'
import { ACTION_LABELS, isOpen, shortName } from '../lib/status.ts'
import type { ButtonTone } from '../telegram/buttons.tsx'
import { useMainButton, useSecondaryButton } from '../telegram/buttons.tsx'
import { openTgLink } from '../telegram/links.ts'
import { Avatar } from '../ui/Avatar.tsx'
import { Button } from '../ui/Button.tsx'
import { Checklist } from '../ui/Checklist.tsx'
import { CommentComposer } from '../ui/CommentComposer.tsx'
import { DeadlineLabel } from '../ui/DeadlineLabel.tsx'
import { EmptyState, ErrorState } from '../ui/EmptyState.tsx'
import { IconAlert, IconChevronDown, IconClock, IconMore, IconPause, IconQuestion } from '../ui/icons.tsx'
import { Section } from '../ui/List.tsx'
import { PriorityLabel } from '../ui/PriorityMark.tsx'
import { Screen, ScreenHeader } from '../ui/ScreenHeader.tsx'
import { SkeletonBlock } from '../ui/Skeleton.tsx'
import { StatusBadge } from '../ui/StatusBadge.tsx'
import { useTaskActions } from '../ui/TaskActions.tsx'
import { Timeline } from '../ui/Timeline.tsx'

/** Подписи для MainButton/SecondaryButton — короткие глаголы без многоточий. */
const BUTTON_LABELS: Partial<Record<TaskCommandType, string>> = {
  accept: 'Принять',
  decline: 'Отклонить',
  start: 'Начать',
  submit: 'Готово',
  verify: 'Принять работу',
  return: 'Вернуть на доработку',
  unblock: 'Блокер снят',
  ask: 'Есть вопрос',
  request_due: 'Перенести срок',
  reopen: 'Вернуть в работу',
}

const TONES: Partial<Record<TaskCommandType, ButtonTone>> = { submit: 'done', verify: 'done' }

interface Plan {
  main: TaskCommandType | null
  secondary: TaskCommandType | null
  more: TaskCommandType[]
}

/** Какие действия вынести в MainButton/SecondaryButton, а какие — в «⋯». Только из allowedActions. */
function actionPlan(t: TaskDetailDto): Plan {
  const allowed = new Set(t.allowedActions)
  const pick = (...c: TaskCommandType[]): TaskCommandType | null => c.find((x) => allowed.has(x)) ?? null
  let main: TaskCommandType | null = null
  let secondary: TaskCommandType | null = null
  if (t.relations.includes('assignee')) {
    switch (t.status) {
      case 'new':
        main = pick('accept')
        secondary = pick('decline')
        break
      case 'accepted':
        main = pick('start')
        secondary = pick('ask', 'submit')
        break
      case 'in_progress':
        main = pick('submit')
        secondary = pick('request_due', 'block')
        break
      case 'blocked':
        main = pick('unblock')
        secondary = pick('ask', 'request_due')
        break
      default:
        break
    }
  }
  if (!main && t.relations.includes('creator')) {
    if (t.status === 'submitted') {
      main = pick('verify')
      secondary = pick('return')
    } else if (!isOpen(t.status)) {
      main = pick('reopen')
    }
  }
  const hidden = new Set<TaskCommandType>(['comment', 'decide_due', 'answer'])
  const more = t.allowedActions.filter((a) => a !== main && a !== secondary && !hidden.has(a))
  return { main, secondary, more }
}

function RequestBanner({
  request,
  task,
  meId,
  canDecide,
  canAnswer,
  onApprove,
  onReject,
  onAnswer,
  busy,
}: {
  request: TaskRequestDto
  task: TaskDetailDto
  meId: number
  canDecide: boolean
  canAnswer: boolean
  onApprove: () => void
  onReject: () => void
  onAnswer: () => void
  busy: boolean
}) {
  const mine = request.requestedBy.id === meId
  const who = mine ? 'Вы' : shortName(request.requestedBy.displayName)
  if (request.kind === 'due_change') {
    return (
      <div className="banner banner--attn" role="status">
        <span className="banner__icon">
          <IconClock size={22} />
        </span>
        <div className="banner__body">
          <div className="banner__title">
            {mine ? 'Вы предложили новый срок' : `${who} просит перенести срок`}
            {request.proposedDueAt && ` на ${formatDue(request.proposedDueAt)}`}
          </div>
          {request.text && <div className="prewrap">{request.text}</div>}
          {task.dueAt && <div className="hint">Сейчас: {formatDue(task.dueAt, task.dueKind)}</div>}
          {canDecide ? (
            <div className="banner__actions">
              <Button variant="primary" size="sm" onClick={onApprove} loading={busy}>
                Согласовать
              </Button>
              <Button variant="secondary" size="sm" onClick={onReject} disabled={busy}>
                Оставить прежний
              </Button>
            </div>
          ) : (
            mine && <div className="hint">Ждём решения постановщика.</div>
          )}
        </div>
      </div>
    )
  }
  return (
    <div className="banner" role="status">
      <span className="banner__icon">
        <IconQuestion size={22} />
      </span>
      <div className="banner__body">
        <div className="banner__title">{mine ? 'Ваш вопрос' : `Вопрос от ${who}`}</div>
        {request.text && <div className="prewrap">{request.text}</div>}
        <div className="hint">{formatMoment(request.createdAt)}</div>
        {canAnswer ? (
          <div className="banner__actions">
            <Button variant="primary" size="sm" onClick={onAnswer}>
              Ответить
            </Button>
          </div>
        ) : (
          mine && <div className="hint">Ждём ответа постановщика.</div>
        )}
      </div>
    </div>
  )
}

function Description({ text }: { text: string }) {
  const [open, setOpen] = useState(false)
  const long = text.length > 220 || text.split('\n').length > 4
  return (
    <Section id="desc" title="Описание" padded>
      <div className={`prewrap${long && !open ? ' clamp-4' : ''}`}>{text}</div>
      {long && (
        <button
          type="button"
          className="collapsible__toggle"
          aria-expanded={open}
          onClick={() => setOpen(!open)}
        >
          {open ? 'Свернуть' : 'Показать полностью'}
          <IconChevronDown size={18} className={open ? 'rotate-180' : undefined} />
        </button>
      )}
    </Section>
  )
}

export function TaskDetailScreen() {
  const { id } = useParams()
  const taskId = Number(id)
  const me = useMe()
  const meId = me.data?.id ?? 0
  const q = useTask(Number.isFinite(taskId) ? taskId : null)
  const task = q.data
  const actions = useTaskActions(meId)
  const comment = useAddComment(taskId)
  const [text, setText] = useState('')
  const now = useNow()

  const plan: Plan = task ? actionPlan(task) : { main: null, secondary: null, more: [] }
  const composing = text.trim().length > 0

  const send = (): void => {
    const body = text.trim()
    if (!body) return
    setText('')
    comment.mutate({ text: body }, { onError: () => setText(body) })
  }

  useMainButton(
    composing
      ? { text: 'Отправить', onClick: send, loading: comment.isPending }
      : task && plan.main
        ? {
            text: BUTTON_LABELS[plan.main] ?? ACTION_LABELS[plan.main],
            onClick: () => plan.main && actions.run(task, plan.main),
            loading: actions.isPending,
            tone: TONES[plan.main],
          }
        : { text: '', onClick: () => undefined, visible: false },
  )
  useSecondaryButton(
    !composing && task && plan.secondary
      ? {
          text: BUTTON_LABELS[plan.secondary] ?? ACTION_LABELS[plan.secondary],
          onClick: () => plan.secondary && actions.run(task, plan.secondary),
          disabled: actions.isPending,
          tone: plan.secondary === 'decline' ? 'danger' : 'default',
        }
      : { text: '', onClick: () => undefined, visible: false },
  )

  if (q.isPending || me.isPending) {
    return (
      <Screen label="Задача">
        <ScreenHeader back title="Задача" small />
        <SkeletonBlock lines={4} />
        <SkeletonBlock lines={3} />
      </Screen>
    )
  }
  if (q.isError || !task) {
    const e = q.error
    const notFound = e instanceof ApiError && (e.code === 'NOT_FOUND' || e.code === 'FORBIDDEN')
    return (
      <Screen label="Задача">
        <ScreenHeader back title="Задача" small />
        {notFound ? (
          <EmptyState
            title={e.code === 'FORBIDDEN' ? 'Нет доступа к задаче' : 'Задача не найдена'}
            text="Возможно, её отменили или передали. Проверьте списки «Мои» и «Поставил»."
          />
        ) : (
          <ErrorState text={errorMessage(e)} onRetry={() => void q.refetch()} />
        )}
      </Screen>
    )
  }

  const isAssignee = task.relations.includes('assignee')
  const isCreator = task.relations.includes('creator')
  const closed = !isOpen(task.status)
  const overdue = task.isOverdue && task.status !== 'submitted'
  const pending = task.requests.filter((r) => r.status === 'pending')
  const results = task.timeline.filter((i) => i.kind === 'comment' && i.commentKind === 'result')
  const resultFiles = task.attachments.filter((a) => a.isResult)
  const allowed = new Set(task.allowedActions)

  return (
    <Screen label="Задача">
      <ScreenHeader
        back
        small
        title={`Задача #${task.id}`}
        actions={
          plan.more.length > 0 ? (
            <button
              type="button"
              className="icon-btn"
              aria-label="Ещё действия"
              onClick={() => actions.openMenu(task, plan.more, false)}
            >
              <IconMore size={24} />
            </button>
          ) : undefined
        }
      />

      <div className="task-head">
        <div className="task-head__badges">
          <StatusBadge status={task.status} />
          {task.priority !== 'normal' && <PriorityLabel priority={task.priority} />}
          {task.requiresAcceptance && (
            <span className="tag">
              {task.reviewer ? `Проверяет: ${task.reviewer.displayName}` : 'Нужна проверка'}
            </span>
          )}
          {task.requiresResult && <span className="tag">Нужен результат</span>}
          {task.returnedCount > 0 && <span className="tag">Возвращалась: {task.returnedCount}</span>}
          {task.publishCheck && (
            <span className="tag">
              📣{' '}
              {task.publishCheck.status === 'matched'
                ? 'Пост найден'
                : task.publishCheck.status === 'disputed'
                  ? 'Пост на подтверждении'
                  : `Ждём пост${task.publishCheck.channel ? ` в «${task.publishCheck.channel.title}»` : ''}`}
            </span>
          )}
          {task.project && (
            <Link className="tag" to={`/projects/${task.project.id}`}>
              🏷 {task.project.name}
            </Link>
          )}
        </div>
        <h2 className={`task-head__title${closed ? ' task-head__title--closed' : ''}`}>{task.title}</h2>
        <DeadlineLabel
          dueAt={task.dueAt}
          dueKind={task.dueKind}
          status={task.status}
          isOverdue={task.isOverdue}
          now={now}
          showNone
        />
        {overdue && task.dueAt && (
          <div className="banner banner--attn">
            <span className="banner__icon">
              <IconAlert size={22} />
            </span>
            <div className="banner__body">
              <div>
                {isAssignee
                  ? `${overdueText(task.dueAt, now).replace(/^с/, 'С')} — обновите срок или статус, так постановщику будет понятнее.`
                  : `${overdueText(task.dueAt, now).replace(/^с/, 'С')}. Можно спросить, нужна ли помощь, или договориться о новом сроке.`}
              </div>
              {isAssignee && allowed.has('request_due') && (
                <div className="banner__actions">
                  <Button size="sm" variant="secondary" onClick={() => actions.run(task, 'request_due')}>
                    Предложить новый срок
                  </Button>
                </div>
              )}
              {isCreator && allowed.has('set_due') && (
                <div className="banner__actions">
                  <Button size="sm" variant="secondary" onClick={() => actions.run(task, 'set_due')}>
                    Изменить срок
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
        <div className="people-line">
          <span className="person">
            <Avatar name={task.creator.displayName} id={task.creator.id} size={24} />
            {task.creator.id === meId ? 'Вы' : task.creator.displayName}
          </span>
          <span className="arrow" aria-hidden="true">
            →
          </span>
          <span className="person">
            <Avatar name={task.assignee.displayName} id={task.assignee.id} size={24} />
            {task.assignee.id === meId ? 'вам' : task.assignee.displayName}
          </span>
          {task.coAssignees.map((u) => (
            <span key={u.id} className="person">
              <Avatar name={u.displayName} id={u.id} size={24} />
              {u.id === meId ? 'вам' : shortName(u.displayName)}
            </span>
          ))}
        </div>
        {task.coAssignees.length > 0 && (
          <div className="hint">
            {task.status === 'new'
              ? 'Задача общая: первый, кто возьмёт, её ведёт; закрыть может любой.'
              : `Задача общая, ведёт ${task.assignee.id === meId ? 'вы' : shortName(task.assignee.displayName)}; закрыть может любой.`}
          </div>
        )}
        {task.watchers.length > 0 && (
          <div className="hint">Наблюдают: {task.watchers.map((w) => w.displayName).join(', ')}</div>
        )}
      </div>

      {task.status === 'blocked' && task.blockedReason && (
        <div className="banner banner--attn">
          <span className="banner__icon">
            <IconPause size={22} />
          </span>
          <div className="banner__body">
            <div className="banner__title">Есть блокер</div>
            <div className="prewrap">{task.blockedReason}</div>
          </div>
        </div>
      )}

      {pending.map((r) => (
        <RequestBanner
          key={r.id}
          request={r}
          task={task}
          meId={meId}
          canDecide={r.kind === 'due_change' && allowed.has('decide_due')}
          canAnswer={r.kind === 'question' && allowed.has('answer')}
          busy={actions.isPending}
          onApprove={() => actions.run(task, 'decide_due', { requestId: r.id, approve: true })}
          onReject={() => actions.run(task, 'decide_due', { requestId: r.id, approve: false })}
          onAnswer={() => actions.run(task, 'answer', { requestId: r.id })}
        />
      ))}

      {task.dod && (
        <Section id="dod" title="Готово, когда" padded>
          <div className="prewrap">{task.dod}</div>
        </Section>
      )}

      <Checklist task={task} meId={meId} />

      {task.description && <Description text={task.description} />}

      {task.source && (task.source.title || task.source.quote) && (
        <Section id="source" title="Откуда задача" padded>
          <div className="stack">
            {task.source.title && <div className="muted">{task.source.title}</div>}
            {task.source.quote && <div className="prewrap">«{task.source.quote}»</div>}
            {task.source.url && (
              <Button onClick={() => openTgLink(task.source!.url!)}>🔗 Открыть исходное сообщение</Button>
            )}
          </div>
        </Section>
      )}

      {(results.length > 0 || resultFiles.length > 0) && (
        <Section id="result" title="Результат" padded>
          <div className="stack">
            {results.map((r) =>
              r.kind === 'comment' ? (
                <div key={r.id} className="prewrap">
                  {r.body}
                </div>
              ) : null,
            )}
            {resultFiles.map((f) => (
              <div key={f.id} className="row">
                <span className="tag tag--done">Файл</span>
                <span className="grow">{f.fileName ?? 'Вложение'}</span>
                {f.size !== null && (
                  <span className="hint num">{Math.max(1, Math.round(f.size / 1024))} КБ</span>
                )}
              </div>
            ))}
            {resultFiles.length > 0 && <div className="hint">Файлы открываются в чате с ботом.</div>}
          </div>
        </Section>
      )}

      <Section id="history" title="История и комментарии">
        <Timeline items={task.timeline} meId={meId} />
      </Section>

      {allowed.has('comment') && <CommentComposer value={text} onChange={setText} onSend={send} />}

      {actions.element}
    </Screen>
  )
}
